import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const taskTemplateKeys = {
  all: ['taskTemplates'],
  lists: () => [...taskTemplateKeys.all, 'list'],
  details: () => [...taskTemplateKeys.all, 'detail'],
  detail: (id) => [...taskTemplateKeys.details(), id],
  byType: (type) => [...taskTemplateKeys.all, 'type', type],
  withCount: () => [...taskTemplateKeys.all, 'withCount'],
};

/**
 * Hook para obtener todas las plantillas de tareas
 */
export const useTaskTemplates = () => {
  return useQuery({
    queryKey: taskTemplateKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('task_templates');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener una plantilla específica
 * @param {string} id - UUID de la plantilla
 * @param {Object} options - Opciones adicionales para la query
 */
export const useTaskTemplate = (id, options = {}) => {
  return useQuery({
    queryKey: taskTemplateKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`task_templates/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener plantillas por tipo
 * @param {string} templateType - Tipo de plantilla ('progress' o 'quality')
 * @param {Object} options - Opciones adicionales para la query
 */
export const useTaskTemplatesByType = (templateType, options = {}) => {
  return useQuery({
    queryKey: taskTemplateKeys.byType(templateType),
    queryFn: async () => {
      const response = await apiClient.get(`task_templates/by-type/${templateType}`);
      return response.data;
    },
    enabled: !!templateType,
    staleTime: 10 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener plantillas con conteo de tareas
 */
export const useTaskTemplatesWithCount = () => {
  return useQuery({
    queryKey: taskTemplateKeys.withCount(),
    queryFn: async () => {
      const response = await apiClient.get('task_templates/with-task-count');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para crear una plantilla de tareas
 */
export const useCreateTaskTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateData) => {
      const response = await apiClient.post('task_templates', templateData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskTemplateKeys.withCount() });
    },
    onError: (error) => {
      console.error('Error creating task template:', error);
    },
  });
};

/**
 * Hook para actualizar una plantilla de tareas
 */
export const useUpdateTaskTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`task_templates/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(taskTemplateKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: taskTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskTemplateKeys.withCount() });
    },
    onError: (error) => {
      console.error('Error updating task template:', error);
    },
  });
};

/**
 * Hook para eliminar una plantilla de tareas
 */
export const useDeleteTaskTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`task_templates/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: taskTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskTemplateKeys.withCount() });
      queryClient.removeQueries({ queryKey: taskTemplateKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting task template:', error);
    },
  });
};